import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, Award } from "lucide-react";

export default function Footer() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error("Fejl i footer ydelser:", err));

    // Load iubenda script loader
    const script = document.createElement("script");
    script.src = "https://cdn.iubenda.com/iubenda.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      {/* Google Maps Sektion */}
      <section className="w-full overflow-hidden leading-none">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2223.4795321528657!2d8.98595567711417!3d56.1314989732284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464bba2650091391%3A0x63185392b4a7818!2zw4VraXJrZWJ5dmVqIDQsIDc0MDAgSGVybmluZw!5e0!3m2!1sda!2sdk!4v1719400000000!5m2!1sda!2sdk"
          style={{ border: 0, width: "100vw", height: "300px" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="BMT TAG ApS Beliggenhed"
          className="block max-w-full grayscale hover:grayscale-0 transition-all duration-300"
        ></iframe>
      </section>

      <footer className="bg-brand-blue-dark text-slate-300 border-t border-brand-blue-light">
      {/* Hovedindhold i footer */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Om os & Logo */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/logo.svg"
                alt="BMT TAG ApS Logo"
                className="h-10 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-slate-400">
              BMT TAG ApS har siden 1986 leveret professionel tagdækning med tagpap af højeste kvalitet til både private og erhverv i Herning og omegn.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <a
                href="https://www.danskindustri.dk/brancher/di-byggeri/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/danskbyggeri.svg"
                  alt="Dansk Byggeri Medlem"
                  className="h-10 w-auto bg-slate-200 p-1.5 rounded-none"
                />
              </a>
              <a
                href="https://inta.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/inta-logo.png"
                  alt="INTA Medlem"
                  className="h-10 w-auto bg-white/10 p-1.5 rounded-none"
                />
              </a>
            </div>
          </div>

          {/* Arbejdsopgaver */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Arbejdsopgaver
            </h3>
            <ul className="space-y-2 text-sm">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/arbejdsopgaver/${service.slug_name}`}
                    className="hover:text-white/50 transition-colors"
                  >
                    {service.title.da}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Praktisk info */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Praktisk info
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privat" className="hover:text-white/50 transition-colors">
                  Privat tagdækning
                </Link>
              </li>
              <li>
                <Link to="/erhverv" className="hover:text-white/50 transition-colors">
                  Erhvervstagdækning
                </Link>
              </li>
              <li>
                <Link to="/medarbejdere" className="hover:text-white/50 transition-colors">
                  Medarbejdere
                </Link>
              </li>
              <li>
                <Link to="/godt-at-vide" className="hover:text-white/50 transition-colors">
                  Godt at vide (Info)
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="hover:text-white/50 transition-colors">
                  Referencer & Galleri
                </Link>
              </li>
            </ul>
          </div>

          {/* Kontakt & Virksomhedsoplysninger */}
          <div className="space-y-3 text-sm">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Virksomhedsoplysninger
            </h3>
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-white shrink-0 mt-0.5" />
              <p>
                BMT TAG ApS<br />
                Åkirkebyvej 4<br />
                7400 Herning
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-white shrink-0" />
              <a href="tel:+4523287183" className="hover:text-white/70 transition-colors whitespace-nowrap">
                23 28 71 83
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-white shrink-0" />
              <a href="mailto:mb@bmttag.dk" className="hover:text-white/70 transition-colors whitespace-nowrap">
                mb@bmttag.dk
              </a>
            </div>
            <div className="pt-2 border-t border-brand-blue-light text-xs text-slate-400 space-y-1">
              <p>CVR-nr.: 45546845</p>
              <p>PBS nr.: 85333186</p>
            </div>
          </div>
        </div>

        {/* Bund-bar */}
        <div className="mt-12 pt-8 border-t border-brand-blue-light flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <p>
            &copy; {new Date().getFullYear()} BMT TAG ApS. Alle rettigheder forbeholdes.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.iubenda.com/privacy-policy/38322055/cookie-policy"
              className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/38322055"
              className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-white" />
            <span>Op til 15 års garanti på tagarbejde</span>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
